import { useEffect, useState } from 'react'
import { ArrowRight, ExternalLink } from 'lucide-react'
import { Logo } from '@/components/brand/Logo'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

/**
 * Pagina-ponte di migrazione dominio.
 *
 * Mostrata al posto dell'app quando `env.redirectTo` è valorizzato (sul vecchio
 * dominio areaclienti.techus.it): avvisa l'utente dello spostamento e
 * reindirizza automaticamente al nuovo dominio dopo un breve countdown,
 * preservando il path corrente (così un bookmark a /bollette atterra su
 * <nuovo>/bollette). È sempre disponibile anche il link manuale.
 *
 * Attivata via flag env (VITE_REDIRECT_TO): spenta finché il nuovo dominio non
 * è pronto e testato.
 */
export function DomainRedirect({ target }: { target: string }) {
  const [seconds, setSeconds] = useState(5)

  // URL di destinazione: nuovo dominio + path/hash correnti.
  const destination = (() => {
    try {
      const base = new URL(target)
      base.pathname = window.location.pathname
      base.search = window.location.search
      base.hash = window.location.hash
      return base.toString()
    } catch {
      return target
    }
  })()

  useEffect(() => {
    if (seconds <= 0) {
      window.location.replace(destination)
      return
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [seconds, destination])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="flex w-full max-w-md flex-col items-center gap-5 p-8 text-center">
        <Logo size="md" />
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-bold text-foreground">Ci siamo spostati</h1>
          <p className="text-sm text-muted-foreground">
            L’area clienti Enermed ha un nuovo indirizzo. Verrai reindirizzato
            automaticamente tra {seconds} second{seconds === 1 ? 'o' : 'i'}.
          </p>
          <p className="break-all text-sm font-semibold text-secondary">
            {new URL(target).host}
          </p>
        </div>

        <Button className="w-full" onClick={() => window.location.replace(destination)}>
          Vai al nuovo sito
          <ArrowRight className="h-4 w-4" />
        </Button>

        <a
          href={destination}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Aggiorna i tuoi preferiti con il nuovo indirizzo
        </a>
      </Card>
    </div>
  )
}
